// SearchStoryList.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import MessageCard from '../container/MessageCard.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import withRouter from '../component/WithRouter.jsx';
import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import Loading from '../component/Loading.jsx';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { CustomImpression } from '../resource/impressionConstants.js';

import {
  color,
  textColor,
  searchStoryMessageCardWidth,
  searchStoryMessageCardWidthTablet,
  searchStoryMessageCardWidthMobile,
  searchStoryMessageCardsColumnGap,
  searchStoryMessageCardsHorizontalTotalPadding,
} from '../style/variables.js';
import ResetHeadingStyle from '../style/ResetHeadingStyle.js';
import media from '../style/media.js';
import { searchType } from '../resource/searchConstants.js';
import MoreButtonIconSrouce from '../../img/ic_next_white.svg';

const isServer = typeof window === 'undefined';

export class SearchStoryList extends React.PureComponent {
  handleLoadMore = () => {
    const {
      sortingString,
      query,
      defaultCategory,
      nextPage,
      hasQuery,
      fetchMessageSearch,
      fetchFeeds,
    } = this.props;
    if (hasQuery) {
      return fetchMessageSearch({
        query,
        type: 'story',
        queryUrl: nextPage,
        sortings: sortingString.split('&').filter(i => i),
      });
    }
    return fetchFeeds({
      page: nextPage,
      type: defaultCategory,
    });
  };
  render() {
    const {
      query,
      location: { search },
      defaultCategory,
      storyRenderAmount,
      nextPage,
      hasQuery,
      isOnSearchAll,
      isNextPageFetching,
      resultIds,
      defaultIds,
      t,
    } = this.props;
    const list = hasQuery ? resultIds : defaultIds;
    if (
      list.length === 0 ||
      (!isServer && isOnSearchAll && !storyRenderAmount)
    ) {
      return null;
    }
    const items = (
      isOnSearchAll && !isServer ? list.slice(0, storyRenderAmount) : list
    ).map((id, index) => (
      <WithIntersectionObserver key={id} threshold={0.2}>
        {({ isIntersecting }) => {
          return (
            <HydrationBoundary
              shouldHydrate={isIntersecting}
              wrapper={<ItemWrapper />}
            >
              <MessageCardWrapper>
                <MessageCard
                  id={id}
                  categoryId={
                    hasQuery ? CustomImpression.STORY_SEARCH : defaultCategory
                  }
                  itemIndexInCategory={index}
                  listPath={
                    hasQuery
                      ? ['search', 'message', searchType.STORY, query]
                      : ['home', 'messages', defaultCategory]
                  }
                  shouldWrapLink
                  shouldShowUser
                  shouldTrackEvent
                  matchedString={query}
                />
              </MessageCardWrapper>
            </HydrationBoundary>
          );
        }}
      </WithIntersectionObserver>
    ));
    return (
      <StyledSearchStoryList isOnSearchAll={isOnSearchAll}>
        {hasQuery ? (
          isOnSearchAll ? (
            <TitleWrapper>
              <Title>
                {t('broadcast', {
                  ns: TranslationNamespace.SEARCH,
                })}
              </Title>
              <MoreButton
                data-element_id={ButtonId.Search.ButtonMoreStory}
                to={{ pathname: `/search/${searchType.STORY}`, search }}
                replace
              >
                {t('btn_show_more', {
                  ns: TranslationNamespace.SEARCH,
                })}{' '}
                <img src={MoreButtonIconSrouce} alt="nore" />
              </MoreButton>
            </TitleWrapper>
          ) : (
            <TitleWrapper paddingBottom={0} />
          )
        ) : (
          <TitleWrapper>
            <Title>
              {t('hot_searches', {
                ns: TranslationNamespace.SEARCH,
              })}
            </Title>
          </TitleWrapper>
        )}
        <ListWrapper>
          {isOnSearchAll ? (
            <ItemsWrapper>{items}</ItemsWrapper>
          ) : (
            <InfiniteScroller
              pageStart={1}
              hasMore={nextPage !== null}
              loadMore={this.handleLoadMore}
              isFetching={isNextPageFetching}
              threshold={100}
            >
              <ItemsWrapper>
                <ScrollItem
                  loader={
                    <ItemWrapper>
                      <LoadingWrapper>
                        <Loading />
                      </LoadingWrapper>
                    </ItemWrapper>
                  }
                >
                  {items}
                </ScrollItem>
              </ItemsWrapper>
            </InfiniteScroller>
          )}
        </ListWrapper>
      </StyledSearchStoryList>
    );
  }
}

SearchStoryList.propTypes = {
  sortingString: PropTypes.string,
  query: PropTypes.string,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  defaultCategory: PropTypes.string,
  storyRenderAmount: PropTypes.number,
  nextPage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isNextPageFetching: PropTypes.bool,
  hasQuery: PropTypes.bool,
  isOnSearchAll: PropTypes.bool,
  resultIds: PropTypes.array,
  defaultIds: PropTypes.array,
  fetchMessageSearch: PropTypes.func,
  fetchFeeds: PropTypes.func,
  t: PropTypes.func.isRequired,
};

SearchStoryList.defaultProps = {
  sortingString: '',
  query: '',
  location: {},
  defaultCategory: null,
  storyRenderAmount: 0,
  nextPage: null,
  isNextPageFetching: false,
  hasQuery: false,
  isOnSearchAll: false,
  resultIds: [],
  defaultIds: [],
  fetchMessageSearch: () => null,
  fetchFeeds: () => null,
};

const StyledSearchStoryList = styled.div`
  padding: 0 ${searchStoryMessageCardsHorizontalTotalPadding / 2}px;
  background-color: ${color.black};
`;

const Title = styled.h1`
  ${ResetHeadingStyle};
  font-size: 16px;
  line-height: 16px;
  font-weight: 600;
  color: ${color.white};
`;
const ListWrapper = styled.main``;

const ItemsWrapper = styled.div`
  margin-bottom: 12px;
  display: grid;
  gap: ${searchStoryMessageCardsColumnGap}px;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${searchStoryMessageCardWidth}px, 1fr)
  );
  ${media.tablet`
    grid-template-columns: repeat(
      auto-fill,
      minmax(${searchStoryMessageCardWidthTablet}px, 1fr)
    );
  `}
  ${media.mobile`
    grid-template-columns: repeat(
      auto-fill,
      minmax(${searchStoryMessageCardWidthMobile}px, 1fr)
    );
  `}
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
`;

const ItemWrapper = styled.div`
  position: relative;
  padding-top: 133%;
`;

const MessageCardWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  padding-bottom: ${({ paddingBottom = 20 }) => `${paddingBottom}px`};
  &:empty {
    padding-top: 0px;
  }
`;

const MoreButton = styled(Link)`
  border-radius: 4px;
  height: 28px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${textColor.white};
  font-size: 12px;
  line-height: 16px;
  border: solid 1px ${color.white};
  > img {
    width: 16px;
    height: 16px;
    margin-left: 4px;
  }
`;

export default withRouter(withTranslation()(SearchStoryList));
